'use client';

import { useTab } from '../../context';
import React from 'react';
import { twMerge } from 'tailwind-merge';

interface TabProps {
  tabSetId: string;
  children: React.ReactNode;
  index: number;
  startDecorator?: React.ReactNode;
  endDecorator?: React.ReactNode;
  className?: string;
  disabled?: boolean;
  variant?: 'default' | 'primary';
  onClick?: () => void;
}

const Tab = ({
  children,
  tabSetId,
  index,
  startDecorator,
  endDecorator,
  className = '',
  disabled,
  variant = 'default',
  onClick,
}: TabProps) => {
  const { isActive, direction, onClick: onTabClick } = useTab(tabSetId, index);

  const handleClick = () => {
    onTabClick();
    onClick?.();
  };

  const isHorizontal = direction === 'horizontal';

  // Base styles that apply to all tabs
  const baseStyles = twMerge(
    'text-sm flex items-center gap-2 transition-colors duration-150 font-semibold',
    disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'
  );

  // Direction-specific styles
  const directionStyles = twMerge(
    isHorizontal ? 'px-2 py-3 text-left' : 'py-2 px-2 w-full text-left'
  );

  // Variant-specific styles
  const variantStyles = {
    default: {
      active: twMerge(
        'text-neutral-600 hover:text-neutral-700',
        isHorizontal ? 'border-b-4 border-sky-600' : ''
      ),
      inactive: 'text-neutral-400 hover:text-neutral-500',
    },
    primary: {
      active:
        'inline-flex justify-center items-center gap-2 self-stretch py-4 bg-sky-600 text-white rounded-tl-xl rounded-tr-xl hover:bg-sky-800',
      inactive:
        'inline-flex justify-center items-center gap-2 self-stretch py-4 bg-neutral-100 text-secondary hover:text-neutral-900 hover:bg-neutral-100 rounded-tl-xl rounded-tr-xl',
    },
  };

  const activeStyle = isActive
    ? variantStyles[variant].active
    : variantStyles[variant].inactive;

  return (
    <button
      role="tab"
      onClick={handleClick}
      className={twMerge(baseStyles, directionStyles, activeStyle, className)}
      disabled={disabled}
      aria-selected={isActive}
    >
      {startDecorator && startDecorator}
      {children}
      {endDecorator && endDecorator}
    </button>
  );
};

export default Tab;
